import type { TFunction } from 'i18next';
import type { ChipTone } from '@cowoo/shared/ui-web';
import type { paths } from '@cowoo/api-client';

/**
 * How a booking row reads (10-web-admin § 9.1).
 *
 * Shared by the list and the panel, and by the "Buchungen heute" table of § 5.2,
 * so the same row never reads two ways on two screens.
 */

export type BookingItem =
  paths['/admin/bookings']['get']['responses']['200']['content']['application/json']['items'][number];

export const CHIPS = ['all', 'desks', 'room', 'day_tickets', 'guest_tickets', 'no_checkin'] as const;
export type BookingChip = (typeof CHIPS)[number];

/** § 9.1 — the status rendering, by tone. */
export const STATUS_TONE: Record<string, ChipTone> = {
  checked_in: 'success',
  checked_out: 'neutral',
  reserved: 'teal',
  paid: 'teal',
  valid: 'neutral',
  pending_payment: 'warn',
  released_no_show: 'warn',
  cancelled: 'danger',
  expired: 'neutral',
  used: 'success',
};

/**
 * The "Objekt" column: "Platz 14 · Fensterreihe", "Meetingraum · 2 Std.",
 * "Gastpass", "Tagesticket · Probetag".
 */
export function objectLabel(booking: BookingItem, t: TFunction): string {
  if (booking.kind === 'guest') return t('admin.bookings.guestPass');
  if (booking.kind === 'room') return t('admin.bookings.room');
  if (booking.isDayTicket) {
    return booking.isTrial ? t('admin.bookings.trialTicket') : t('admin.bookings.dayTicket');
  }
  const desk = t('admin.bookings.desk', { label: booking.label ?? '—' });
  return booking.zone ? `${desk} · ${booking.zone}` : desk;
}

/**
 * The "Wer" column. A guest pass names the guest and its host, because on this
 * screen "who is that" and "whose guest is that" are the same question.
 */
export function whoLabel(booking: BookingItem, t: TFunction): string {
  const member = booking.userName ?? booking.userEmail ?? '—';
  if (booking.kind === 'guest') {
    return t('admin.bookings.guestOf', { guest: booking.guestName ?? '—', host: member });
  }
  if (booking.isDayTicket) return `${t('admin.bookings.dayTicket')} · ${member}`;
  return member;
}
